import {
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useInspectionFreshness,
  type FileFreshness,
  type FreshnessState,
} from '../lib/deadlines-api';

/** The order and colour each freshness bucket renders in. */
const STATES: { state: Exclude<FreshnessState, 'fresh'> | 'fresh'; color: string }[] = [
  { state: 'fresh', color: palette.semantic.success },
  { state: 'aging', color: palette.semantic.warning },
  { state: 'stale', color: palette.semantic.emergency },
  { state: 'none', color: palette.text.muted },
];

function CountPill({ color, count, label }: { color: string; count: number; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: 13, color: palette.text.primary }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {count}
        </Box>{' '}
        {label}
      </Typography>
    </Stack>
  );
}

/** The deep-link nudge for the worst-off inspection, when one needs attention. */
function FreshnessNudge({ file }: { file: FileFreshness }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message =
    file.state === 'none'
      ? t('gov.dashboard.freshness.nudgeNever', { reference: file.reference })
      : t('gov.dashboard.freshness.nudgeAged', {
          reference: file.reference,
          count: file.daysSince ?? 0,
        });

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        mt: 1.75,
        pt: 1.5,
        borderTop: `1px solid ${palette.border.line}`,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
        <Icons.AlertTriangle
          size={iconSize.sm}
          color={palette.semantic.warning}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>{message}</Typography>
      </Stack>
      <Button size="small" variant="text" onClick={() => navigate(`/files/${file.fileId}`)}>
        {t('gov.dashboard.freshness.review')}
      </Button>
    </Stack>
  );
}

/**
 * X4 — the inspection-freshness indicator. Reduces each open file to the age of
 * its most recent evidence capture and summarises how current the caseload's
 * site inspections are, nudging toward the stalest one.
 */
export function InspectionFreshness() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useInspectionFreshness();

  const buckets = STATES.map((s) => ({ ...s, count: data[s.state] })).filter((s) => s.count > 0);
  const needsAttention =
    data.stalest && (data.stalest.state === 'stale' || data.stalest.state === 'none')
      ? data.stalest
      : null;

  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Icons.RefreshCw size={iconSize.lg} color={palette.brand.accent} />
        <Typography variant="h6">{t('gov.dashboard.freshness.title')}</Typography>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
          {t('gov.dashboard.freshness.loadError')}
        </Typography>
      ) : data.tracked === 0 || buckets.every((b) => b.state === 'fresh') ? (
        <Typography variant="body2" sx={{ mt: 1.5, color: palette.text.secondary }}>
          {t('gov.dashboard.freshness.allCurrent')}
        </Typography>
      ) : (
        <>
          <Stack direction="row" sx={{ mt: 1.75, flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
            {buckets.map((b) => (
              <CountPill
                key={b.state}
                color={b.color}
                count={b.count}
                label={t(`gov.dashboard.freshness.${b.state}`)}
              />
            ))}
          </Stack>
          {needsAttention && <FreshnessNudge file={needsAttention} />}
        </>
      )}
    </HairlineCard>
  );
}
